import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export type CurrencyMode = "customer" | "base";

interface CurrencySelectProps {
  /** The customer's own trading currency code, e.g. "USD" */
  customerCurrency: string;
  /** The company's base currency code, e.g. "GBP" */
  baseCurrency: string;
  /** Currently selected mode */
  value: CurrencyMode;
  /** Called when the user picks a different currency */
  onChange: (mode: CurrencyMode) => void;
  /** Optional extra className for the trigger */
  className?: string;
}

/**
 * CurrencySelect
 *
 * A dropdown that lets the user pick between the customer's own currency and
 * the company base currency.
 *
 * Rules:
 *  - If customerCurrency === baseCurrency, only one option is shown (no descriptor).
 *  - Otherwise two options are shown:
 *      "<customerCurrency> (Default)"  – the customer's trading currency
 *      "<baseCurrency> (Base)"         – the company's base / home currency
 */
export function CurrencySelect({
  customerCurrency,
  baseCurrency,
  value,
  onChange,
  className,
}: Readonly<CurrencySelectProps>) {
  const isSameCurrency = customerCurrency === baseCurrency;

  function handleValueChange(selected: string) {
    onChange(selected as CurrencyMode);
  }

  return (
    <Select value={value} onValueChange={handleValueChange}>
      <SelectTrigger className={className ?? "w-auto min-w-[160px]"}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {isSameCurrency ? (
          /* Single option — no descriptor needed */
          <SelectItem value="customer">{customerCurrency}</SelectItem>
        ) : (
          <>
            <SelectItem value="customer">
              {customerCurrency} (Default)
            </SelectItem>
            <SelectItem value="base">
              {baseCurrency} (Base)
            </SelectItem>
          </>
        )}
      </SelectContent>
    </Select>
  );
}
